/**
 * Campaigns Service
 * Handles all fundraising campaign-related API calls
 */

import { ApiClient } from '../core/api-client';
import type { Campaign } from '../../models/all';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

export interface CampaignCreatePayload {
  name: string;
  description?: string;
  targetAmount?: number;
  eventId?: number;
  [key: string]: unknown;
}

class CampaignsService {
  private readonly client: ApiClient;

  constructor(client: ApiClient) {
    this.client = client;
  }

  /**
   * Get all campaigns
   */
  async getCampaigns(signal?: AbortSignal): Promise<Campaign[]> {
    try {
      const response = await this.client.get<unknown>(
        '/devotee/campaign',
        { signal },
      );
      return Array.isArray(response.data) ? (response.data as Campaign[]) : [];
    } catch (error) {
      return [];
    }
  }

  /**
   * Get a single campaign by ID
   */
  async getCampaign(id: number | string, signal?: AbortSignal): Promise<Campaign | null> {
    try {
      const response = await this.client.get<Campaign>(
        `/devotee/campaign/${id}`,
        { signal },
      );
      return response.data || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Create a new campaign
   */
  async createCampaign(
    payload: CampaignCreatePayload,
    signal?: AbortSignal,
  ): Promise<Campaign> {
    const response = await this.client.post<Campaign>(
      '/devotee/campaign',
      payload,
      { signal },
    );
    return response.data;
  }

  /**
   * Update a campaign
   */
  async updateCampaign(
    id: number | string,
    payload: Partial<CampaignCreatePayload>,
    signal?: AbortSignal,
  ): Promise<Campaign> {
    const response = await this.client.put<Campaign>(
      `/devotee/campaign/${id}`,
      payload,
      { signal },
    );
    return response.data;
  }
}

export const campaignsService = new CampaignsService(ApiClient.getInstance(API_BASE_URL));

export type { Campaign };
